import {
  FISH_KINDS,
  FISH_RARITY,
  FISH_RARITY_COLORS,
  ITEM_NAMES,
  isFishItem,
  type FishBoardMessage,
  type InventorySlot,
} from "@ghostland/shared";
import { itemThumb } from "../items/itemThumb.js";

/**
 * OLD TIMER'S BAITS fishing UI:
 *  - the RECORDS SCROLL leaderboard (E at the wall scroll) — per fish kind, the top LARGEST and
 *    top SMALLEST imprinted catches across all players.
 *  - the IMPRINT picker (Old Timer dialog) — choose one fish from the bag to imprint; the fish
 *    is never consumed, only its size is recorded.
 */

const STYLE_ID = "gl-fb-style";
const CSS = `
#gl-fishboard {
  position: fixed; inset: 0; z-index: 40; display: none; align-items: center; justify-content: center;
  background: rgba(0,0,0,.55);
}
#gl-fishboard.open { display: flex; }
#gl-fishboard .card {
  width: min(480px, 92vw); max-height: 82vh; overflow-y: auto;
  background: #0a0a0a; border: 2px solid #f4f4f4; box-shadow: 0 0 0 2px #000;
  color: #f4f4f4; font-family: ui-monospace, monospace; padding: 16px 18px;
}
#gl-fishboard h1 { font-size: 14px; letter-spacing: 3px; margin: 0 0 12px; color: #ffd9a0; text-align: center; }
#gl-fishboard .tabs { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 10px; justify-content: center; }
#gl-fishboard .tab {
  background: #0a0a0a; border: 1px solid #3a444e; color: #9aa4ae; font: inherit; font-size: 10px;
  letter-spacing: .5px; padding: 4px 8px; cursor: pointer;
}
#gl-fishboard .tab.sel { border-color: currentColor; background: rgba(255,255,255,.06); }
#gl-fishboard .anon { display: flex; align-items: center; gap: 8px; margin-top: 10px; font-size: 11px; color: #9aa4ae; cursor: pointer; }
#gl-fishboard .kind { border: 1px solid #3a444e; padding: 8px 10px; margin-bottom: 8px; }
#gl-fishboard .kn { font-size: 12px; letter-spacing: 1px; margin-bottom: 4px; }
#gl-fishboard .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 0 14px; font-size: 11px; }
#gl-fishboard .colhead { color: #9aa4ae; letter-spacing: 1px; font-size: 10px; margin-bottom: 2px; }
#gl-fishboard .row { display: flex; justify-content: space-between; line-height: 1.6; }
#gl-fishboard .row .nm { color: #c7cfd6; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-right: 8px; }
#gl-fishboard .row .sz { color: #7ce0a0; }
#gl-fishboard .empty { color: #5a636d; font-size: 11px; }
#gl-fishboard .hint { text-align: center; color: #9aa4ae; font-size: 10px; letter-spacing: 1px; margin-top: 8px; }
#gl-imprint {
  position: fixed; inset: 0; z-index: 40; display: none; align-items: center; justify-content: center;
  background: rgba(0,0,0,.55);
}
#gl-imprint.open { display: flex; }
#gl-imprint .card {
  width: min(380px, 92vw); max-height: 70vh; overflow-y: auto;
  background: #0a0a0a; border: 2px solid #f4f4f4; box-shadow: 0 0 0 2px #000;
  color: #f4f4f4; font-family: ui-monospace, monospace; padding: 14px 16px;
}
#gl-imprint h1 { font-size: 13px; letter-spacing: 2px; margin: 0 0 10px; color: #7ce0a0; text-align: center; }
#gl-imprint .fish {
  display: flex; align-items: center; gap: 10px; width: 100%; text-align: left;
  background: none; border: 1px solid #3a444e; padding: 6px 8px; margin-bottom: 6px;
  color: #f4f4f4; font: inherit; cursor: pointer;
}
#gl-imprint .fish:hover { border-color: #7ce0a0; background: rgba(124,224,160,.08); }
#gl-imprint .fish canvas { width: 32px; height: 32px; image-rendering: pixelated; flex: none; }
#gl-imprint .fish .sz { margin-left: auto; color: #7ce0a0; }
#gl-imprint .empty { color: #5a636d; font-size: 11px; text-align: center; padding: 10px 0; }
`;

function ensureStyle(): void {
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style");
    s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
}

// ── the RECORDS SCROLL leaderboard ───────────────────────────────────────────
let board: HTMLDivElement | null = null;
let boardData: FishBoardMessage | null = null;
let selectedKind: (typeof FISH_KINDS)[number] = FISH_KINDS[0];
let onAnonymous: ((anonymous: boolean) => void) | null = null;

/** Wired by world3d: the "stay anonymous" toggle (same flag as the wealth rankboard). */
export function setFishBoardActions(a: { setAnonymous: (anonymous: boolean) => void }): void {
  onAnonymous = a.setAnonymous;
}

export function isFishBoardOpen(): boolean { return !!board?.classList.contains("open"); }
export function closeFishBoard(): void { board?.classList.remove("open"); }

/** Open the scroll (empty shell) — fills in when the FishBoard snapshot arrives. */
export function openFishBoard(): void {
  ensureStyle();
  if (!board) {
    board = document.createElement("div");
    board.id = "gl-fishboard";
    board.addEventListener("click", (e) => { if (e.target === board) closeFishBoard(); });
    document.body.appendChild(board);
  }
  boardData = null;
  board.innerHTML = `<div class="card"><h1>FISHING RECORDS</h1><div class="empty">Unrolling the scroll…</div></div>`;
  board.classList.add("open");
}

/** Store the server snapshot and render the selected fish's tab. */
export function setFishBoard(data: FishBoardMessage): void {
  boardData = data;
  renderBoard();
}

function renderBoard(): void {
  if (!board || !isFishBoardOpen() || !boardData) return;
  const esc = (t: string): string => t.replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]!));
  const card = document.createElement("div");
  card.className = "card";
  card.innerHTML = `<h1>FISHING RECORDS</h1>`;

  // fish tabs (tier order — ghost koi last), colored by rarity
  const tabs = document.createElement("div");
  tabs.className = "tabs";
  for (const kind of FISH_KINDS) {
    const b = document.createElement("button");
    b.className = "tab" + (kind === selectedKind ? " sel" : "");
    b.textContent = ITEM_NAMES[kind] ?? kind;
    b.style.color = FISH_RARITY_COLORS[FISH_RARITY[kind]];
    b.onclick = () => { selectedKind = kind; renderBoard(); };
    tabs.appendChild(b);
  }
  card.appendChild(tabs);

  // the selected fish: top 5 largest + top 5 smallest, ranked, with names
  const k = boardData.kinds.find((x) => x.kind === selectedKind);
  const list = (entries: { name: string; size: number }[]): string =>
    entries.length
      ? entries.map((e, i) =>
          `<div class="row"><span class="nm">#${i + 1} ${e.name ? esc(e.name) : "— anonymous —"}</span>` +
          `<span class="sz">${e.size.toFixed(2)}&#8243;</span></div>`).join("")
      : `<div class="empty">no imprints yet</div>`;
  const body = document.createElement("div");
  body.className = "kind";
  body.innerHTML =
    `<div class="kn" style="color:${FISH_RARITY_COLORS[FISH_RARITY[selectedKind]]}">${ITEM_NAMES[selectedKind] ?? selectedKind}</div>` +
    `<div class="cols"><div><div class="colhead">LARGEST</div>${list(k?.largest ?? [])}</div>` +
    `<div><div class="colhead">SMALLEST</div>${list(k?.smallest ?? [])}</div></div>`;
  card.appendChild(body);

  // anonymity — the same flag as the wealth rankboard
  const anon = document.createElement("label");
  anon.className = "anon";
  const cb = document.createElement("input");
  cb.type = "checkbox";
  cb.checked = boardData.youAnonymous;
  cb.onchange = () => {
    if (boardData) boardData.youAnonymous = cb.checked;
    onAnonymous?.(cb.checked);
  };
  const txt = document.createElement("span");
  txt.textContent = "Stay anonymous (hide my name)";
  anon.append(cb, txt);
  card.appendChild(anon);

  const hint = document.createElement("div");
  hint.className = "hint";
  hint.textContent = "imprint your catches with the Old Timer";
  card.appendChild(hint);

  board.replaceChildren(card);
}

// ── the IMPRINT picker ───────────────────────────────────────────────────────
let picker: HTMLDivElement | null = null;

export function isImprintOpen(): boolean { return !!picker?.classList.contains("open"); }
export function closeImprint(): void { picker?.classList.remove("open"); }

/** Pick one bagged fish to imprint (never consumed). `onPick` sends the slot to the server. */
export function openImprint(slots: readonly InventorySlot[], onPick: (slotIndex: number) => void, ownCharacterId = ""): void {
  ensureStyle();
  if (!picker) {
    picker = document.createElement("div");
    picker.id = "gl-imprint";
    picker.addEventListener("click", (e) => { if (e.target === picker) closeImprint(); });
    document.body.appendChild(picker);
  }
  const card = document.createElement("div");
  card.className = "card";
  const h = document.createElement("h1");
  h.textContent = "IMPRINT A FISH";
  card.appendChild(h);
  // Only YOUR OWN catches can be imprinted (provenance recorded at catch time; the server
  // enforces this too). Fish with no angler recorded (legacy/traded/bought) don't qualify.
  const fish = slots.filter((s) => isFishItem(s.itemId) && s.size !== undefined && s.caughtBy === ownCharacterId && ownCharacterId !== "");
  if (!fish.length) {
    const none = document.createElement("div");
    none.className = "empty";
    none.textContent = "No fish you caught yourself to imprint.";
    card.appendChild(none);
  }
  for (const s of fish) {
    const b = document.createElement("button");
    b.className = "fish";
    b.appendChild(itemThumb(s.itemId));
    const nm = document.createElement("span");
    nm.textContent = ITEM_NAMES[s.itemId as keyof typeof ITEM_NAMES] ?? s.itemId;
    nm.style.color = FISH_RARITY_COLORS[FISH_RARITY[s.itemId as (typeof FISH_KINDS)[number]]];
    const sz = document.createElement("span");
    sz.className = "sz";
    sz.textContent = `${(s.size ?? 0).toFixed(2)}″`;
    b.append(nm, sz);
    b.onclick = () => { closeImprint(); onPick(s.slotIndex); };
    card.appendChild(b);
  }
  picker.replaceChildren(card);
  picker.classList.add("open");
}
